/**
 * Connection status indicator component.
 */

import { useState, useEffect, useCallback } from 'react';
import { useConnectionStore } from '../../stores/connectionStore';
import { websocketService } from '../../services/websocket';
import type { ConnectionStatus as ConnectionStatusType } from '../../types/websocket';

const STATUS_CONFIG: Record<
  ConnectionStatusType,
  { color: string; bgColor: string; label: string }
> = {
  connected: { color: 'bg-green-500', bgColor: 'bg-green-50 dark:bg-green-900/30', label: 'Connected' },
  connecting: { color: 'bg-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-900/30', label: 'Connecting' },
  reconnecting: { color: 'bg-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-900/30', label: 'Reconnecting' },
  disconnected: { color: 'bg-red-500', bgColor: 'bg-red-50 dark:bg-red-900/30', label: 'Disconnected' },
};

export interface ConnectionStatusProps {
  /** Additional CSS class */
  className?: string;
  /** Show detailed info on hover */
  showDetails?: boolean;
}

export function ConnectionStatus({
  className = '',
  showDetails = true,
}: ConnectionStatusProps) {
  const status = useConnectionStore((state) => state.status);
  const reconnectAttempts = useConnectionStore((state) => state.reconnectAttempts);
  const nextReconnectTime = useConnectionStore((state) => state.nextReconnectTime);
  const lastError = useConnectionStore((state) => state.lastError);

  const [countdown, setCountdown] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Countdown timer for reconnection - all state updates in callbacks only
  useEffect(() => {
    if (status !== 'reconnecting' || !nextReconnectTime) {
      return;
    }

    // All setState calls happen inside interval callback (not synchronously in effect)
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((nextReconnectTime - Date.now()) / 1000));
      setCountdown(remaining);
    }, 100);

    return () => {
      clearInterval(interval);
      setCountdown(null);
    };
  }, [status, nextReconnectTime]);

  const handleClick = useCallback(() => {
    if (status === 'disconnected') {
      websocketService.connect();
    }
  }, [status]);

  const config = STATUS_CONFIG[status];

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Status indicator */}
      <button
        onClick={handleClick}
        disabled={status !== 'disconnected'}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
          ${config.bgColor} border border-gray-200 dark:border-gray-600
          ${status === 'disconnected' ? 'cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/50' : 'cursor-default'}
          transition-colors duration-150
        `}
        aria-label={`Connection status: ${config.label}${
          status === 'disconnected' ? '. Click to reconnect.' : ''
        }`}
      >
        {/* Pulsing dot */}
        <span className="relative flex h-3 w-3">
          {(status === 'connecting' || status === 'reconnecting') && (
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.color} opacity-75`}
            />
          )}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${config.color}`} />
        </span>

        <span className="text-gray-700 dark:text-gray-200">{config.label}</span>

        {/* Countdown */}
        {countdown !== null && countdown > 0 && (
          <span className="text-gray-500 dark:text-gray-400 text-xs">({countdown}s)</span>
        )}
      </button>

      {/* Details tooltip */}
      {showDetails && isHovered && (status === 'reconnecting' || lastError) && (
        <div className="absolute top-full left-0 mt-1 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-xs z-50 min-w-[200px]">
          {status === 'reconnecting' && (
            <p className="text-gray-600 dark:text-gray-300">
              Attempt {reconnectAttempts}/5
              {countdown !== null && ` - Retrying in ${countdown}s`}
            </p>
          )}
          {lastError && <p className="text-red-600 dark:text-red-400 mt-1">{lastError}</p>}
        </div>
      )}
    </div>
  );
}
